import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../Common/Configurations/AuthContext";
import Header from "../../Common/Layout/Header";
import { Link, useParams } from "react-router-dom";
import moment from "moment";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useReactToPrint } from "react-to-print";
import BarcodeGenerator from "./BarcodeGenerator";
import { buildBoardingPassScanText, getPnrCodes } from "./boardingPassBarcode";
import { getProxiedImageUrl, swapProxiedImagesForCapture } from "../../Common/Helpers/imageProxy";

// Neutral, print-friendly palette — no brand colors, just ink/gray on white.
const ink = "#1a1a1a";
const muted = "#666666";
const faint = "#999999";
const line = "#d9d9d9";

const styles = {
  page: { background: "#eef0f2", minHeight: "100vh", padding: "20px 0" },
  actionBar: { display: "flex", justifyContent: "flex-end", gap: "10px", marginBottom: "20px", padding: "0 16px" },
  btn: { display: "inline-flex", alignItems: "center", gap: "6px", padding: "8px 18px", fontWeight: 600, fontSize: "13px", cursor: "pointer", textDecoration: "none" },
  btnSolid: { background: ink, color: "#fff", border: `1px solid ${ink}` },
  btnOutline: { background: "#fff", color: ink, border: `1px solid ${ink}` },
  voucherWrap: { maxWidth: "820px", margin: "0 auto", fontFamily: "'Segoe UI', Arial, sans-serif", color: ink },
  voucherCard: { background: "#fff", border: `1px solid ${ink}`, marginBottom: "28px" },
  voucherHeader: { padding: "26px 32px 20px", borderBottom: `2px solid ${ink}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start" },
  voucherTitle: { fontSize: "12px", letterSpacing: "3px", textTransform: "uppercase", color: muted, marginBottom: "4px" },
  voucherHeading: { fontSize: "24px", fontWeight: 800, letterSpacing: "1px", margin: 0, color: ink },
  voucherSubtitle: { fontSize: "12px", color: muted, marginTop: "4px" },
  agencyBlock: { textAlign: "right" },
  agencyLogo: { maxHeight: "44px", maxWidth: "160px", objectFit: "contain", marginBottom: "8px" },
  agencyName: { fontWeight: 700, fontSize: "16px", color: ink },
  agencyInfo: { fontSize: "12px", color: muted, marginTop: "4px", lineHeight: 1.6 },
  confirmedBadge: { border: `1px solid ${ink}`, padding: "3px 12px", fontSize: "11px", fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", display: "inline-block", marginTop: "10px" },
  bookingMeta: { background: "#f7f7f7", padding: "10px 32px", display: "flex", gap: "32px", flexWrap: "wrap", borderBottom: `1px solid ${line}` },
  metaItem: { color: muted, fontSize: "11px", letterSpacing: "1px", textTransform: "uppercase" },
  metaValue: { color: ink, fontWeight: 700, fontSize: "13px", marginTop: "2px" },
  segmentSection: { padding: "24px 32px 0" },
  routeRow: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" },
  cityBlock: { textAlign: "center", flex: 1 },
  iataCode: { fontSize: "44px", fontWeight: 800, color: ink, letterSpacing: "-1px", lineHeight: 1 },
  cityName: { fontSize: "13px", color: muted, marginTop: "2px", fontWeight: 500 },
  flightArrow: { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" },
  arrowLine: { position: "relative", width: "100%", display: "flex", alignItems: "center" },
  dashedLine: { flex: 1, borderTop: `1px dashed ${faint}`, margin: "0 8px" },
  durationText: { fontSize: "11px", color: faint, fontWeight: 600, letterSpacing: "1px" },
  timeBlock: { textAlign: "center" },
  timeText: { fontSize: "26px", fontWeight: 700, color: ink },
  dateText: { fontSize: "12px", color: muted, marginTop: "2px" },
  airportText: { fontSize: "11px", color: faint, marginTop: "2px", maxWidth: "130px" },
  segmentMeta: { display: "flex", alignItems: "center", gap: "12px", background: "#f7f7f7", padding: "10px 16px", marginBottom: "14px", border: `1px solid ${line}` },
  airlineLogo: { width: "36px", height: "36px", objectFit: "contain", background: "#fff", padding: "2px", border: `1px solid ${line}` },
  airlineName: { fontWeight: 700, fontSize: "14px", color: ink },
  flightNum: { fontSize: "12px", color: muted },
  connectBadge: { border: `1px solid ${ink}`, padding: "3px 12px", fontSize: "11px", fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", margin: "8px 0 4px", display: "inline-block" },
  perforated: { borderTop: `1px dashed ${faint}`, margin: "20px 0" },
  passengerSection: { padding: "16px 32px 0" },
  passTable: { width: "100%", borderCollapse: "collapse" },
  passTh: { background: "#f7f7f7", color: ink, fontSize: "11px", letterSpacing: "1px", textTransform: "uppercase", padding: "8px 12px", textAlign: "left", fontWeight: 700, borderBottom: `2px solid ${ink}` },
  passTd: { padding: "10px 12px", fontSize: "13px", color: ink, borderBottom: `1px solid ${line}`, fontWeight: 500 },
  barcodeSection: { background: ink, padding: "18px 32px", display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: "24px" },
  bookingRef: { color: "#fff", textAlign: "right" },
  bookingRefLabel: { fontSize: "10px", letterSpacing: "2px", opacity: 0.7, textTransform: "uppercase" },
  bookingRefValue: { fontSize: "22px", fontWeight: 800, letterSpacing: "2px", marginTop: "2px" },
  pnrText: { fontSize: "11px", opacity: 0.7, marginTop: "2px" },
  sectionLabel: { fontSize: "11px", letterSpacing: "2px", textTransform: "uppercase", color: muted, fontWeight: 700, marginBottom: "10px" },
  cabinBadge: { border: `1px solid ${ink}`, padding: "3px 10px", fontSize: "11px", fontWeight: 700, color: ink },
};

const FlightVoucher = () => {
  const params = useParams();
  const contentRef = useRef();
  const [pageLoading, setPageLoading] = useState(false);
  const { user, loading } = useAuth();
  const [bookingDetails, setBookingDetails] = useState({});
  const [travellers, setTravellers] = useState([]);
  const [itinerary, setItinerary] = useState([]);

  const handlePrint = useReactToPrint({ content: () => contentRef.current });

  const handlePDF = () => {
    const input = contentRef.current;
    html2canvas(input, { useCORS: true, onclone: swapProxiedImagesForCapture }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF();
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      pdf.save(bookingDetails.travellerName + "_ticket_voucher.pdf");
    });
  };

  function getTimeFromMins(mins) {
    var h = (mins / 60) | 0, m = mins % 60 | 0;
    return h + "h " + m + "m";
  }

  const GetData = () => {
    setPageLoading(true);
    apiService
      .getAll(config.GETFLIGHTBOOKINGDETAILS + "/" + params.flightBookingId)
      .then((res) => {
        setBookingDetails(res);
        setItinerary(res.dcFlightLegs);
        setTravellers(res.travellerInfos);
        setPageLoading(false);
      })
      .catch((err) => console.error("Error fetching company data", err));
  };

  useEffect(() => { GetData(); }, [user, loading]);

  if (loading) return <p>Loading...</p>;
  if (!user) return <p>Please log in</p>;

  return (
    <div id="layout-wrapper">
      <Header User={user} />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="col-xl-12">
              <div className="card">
                <div className="card-body" style={styles.page}>
                  {pageLoading ? (
                    <div className="row align-items-center justify-content-center">
                      <div className="col-lg-12">
                        <img src="/assets/images/loader.webp" width="64px" height="64px" alt="loading" loading="lazy" />
                        Loading, Please wait...
                      </div>
                    </div>
                  ) : (
                    <>
                      <div style={styles.actionBar} className="d-print-none">
                        <Link style={{ ...styles.btn, ...styles.btnOutline }} onClick={handlePrint}>
                          <i className="ri-printer-line"></i> Print
                        </Link>
                        <Link style={{ ...styles.btn, ...styles.btnSolid }} onClick={handlePDF}>
                          <i className="ri-download-2-line"></i> Download Voucher
                        </Link>
                        <Link to={"/flight/invoice/" + params.flightBookingId} style={{ ...styles.btn, ...styles.btnOutline }}>
                          <i className="ri-file-text-line"></i> View Invoice
                        </Link>
                        <Link to={"/flight/customer-invoice/" + params.flightBookingId} style={{ ...styles.btn, ...styles.btnOutline }}>
                          <i className="ri-file-text-line"></i> View Customer Invoice
                        </Link>
                      </div>

                      <div ref={contentRef} style={styles.voucherWrap}>
                        {itinerary.length > 0 && itinerary.map((it, itIdx) =>
                          it.dcDisplayLegs?.length > 0 && it.dcDisplayLegs.map((leg, legIdx) => (
                            <div key={`${itIdx}-${legIdx}`} style={styles.voucherCard}>
                              {/* Header */}
                              <div style={styles.voucherHeader}>
                                <div>
                                  <div style={styles.voucherTitle}>Flight Ticket Voucher</div>
                                  <div style={styles.voucherHeading}>Airline Ticket</div>
                                  <div style={styles.voucherSubtitle}>
                                    {moment(leg.startSegment.departureDateTime).format("dddd, DD MMMM YYYY")}
                                  </div>
                                  <div style={styles.confirmedBadge}>
                                    {bookingDetails.bookingStatus}
                                  </div>
                                </div>
                                <div style={styles.agencyBlock}>
                                  {user?.logo && (
                                    <img src={user.logo} data-proxy-src={getProxiedImageUrl(user.logo)} alt={user?.companyname || "Logo"} style={styles.agencyLogo} loading="lazy" />
                                  )}
                                  <div style={styles.agencyName}>{user?.companyname}</div>
                                  <div style={styles.agencyInfo}>
                                    {user?.email}<br />
                                    {user?.phoneNumber}<br />
                                    GST: {user?.companygstno}
                                  </div>
                                </div>
                              </div>

                              {/* Booking Meta */}
                              <div style={styles.bookingMeta}>
                                {bookingDetails.bookingNo && (
                                  <div style={styles.metaItem}>
                                    Booking No<div style={styles.metaValue}>{bookingDetails.bookingNo}</div>
                                  </div>
                                )}
                                {bookingDetails.pnr && (
                                  <div style={styles.metaItem}>
                                    PNR<div style={styles.metaValue}>{bookingDetails.pnr}</div>
                                  </div>
                                )}
                                {bookingDetails.supplierBookingId && (
                                  <div style={styles.metaItem}>
                                    Supplier Ref<div style={styles.metaValue}>{bookingDetails.supplierBookingId}</div>
                                  </div>
                                )}
                                {bookingDetails.cabinClass && (
                                  <div style={styles.metaItem}>
                                    Class<div style={styles.metaValue}>{bookingDetails.cabinClass}</div>
                                  </div>
                                )}
                                <div style={styles.metaItem}>
                                  Status
                                  <div style={styles.metaValue}>CONFIRMED</div>
                                </div>
                                <div style={styles.metaItem}>
                                  Refund Policy
                                  <div style={styles.metaValue}>
                                    {it.flightLeg?.refundable || "—"}
                                  </div>
                                </div>
                              </div>

                              {/* Route */}
                              <div style={styles.segmentSection}>
                                <div style={styles.sectionLabel}>Flight Route</div>
                                <div style={styles.routeRow}>
                                  <div style={styles.cityBlock}>
                                    <div style={styles.iataCode}>{leg.startSegment.airportCode}</div>
                                    <div style={styles.cityName}>{leg.startSegment.city}</div>
                                  </div>
                                  <div style={styles.flightArrow}>
                                    <div style={styles.arrowLine}>
                                      <div style={styles.dashedLine}></div>
                                    </div>
                                    <div style={styles.durationText}>DIRECT FLIGHT</div>
                                  </div>
                                  <div style={styles.cityBlock}>
                                    <div style={styles.iataCode}>{leg.endSegment.airportCode}</div>
                                    <div style={styles.cityName}>{leg.endSegment.city}</div>
                                  </div>
                                </div>

                                {/* Segments */}
                                {it.dcSegments?.length > 0 && it.dcSegments.map((seg, segIdx) => (
                                  <div key={segIdx}>
                                    <div style={styles.segmentMeta}>
                                      <img
                                        style={styles.airlineLogo}
                                        src={"/assets/images/airlinelogos/" + seg.tFAirlineData?.airlineCode + ".png"}
                                        alt={seg.tFAirlineData?.airline}
                                        onError={(e) => { e.target.style.display = "none"; }}
                                      loading="lazy" />
                                      <div style={{ flex: 1 }}>
                                        <div style={styles.airlineName}>{seg.tFAirlineData?.airline}</div>
                                        <div style={styles.flightNum}>
                                          {seg.tFAirlineData?.equimentType}-{seg.tFAirlineData?.flightNumber}
                                        </div>
                                      </div>
                                      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                        <span style={styles.cabinBadge}>{bookingDetails.cabinClass}</span>
                                      </div>
                                    </div>

                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 8px 20px", gap: "8px" }}>
                                      <div style={styles.timeBlock}>
                                        <div style={styles.timeText}>
                                          {moment(seg.tFDepartureData.departureDateTime).format("HH:mm")}
                                        </div>
                                        <div style={styles.dateText}>
                                          {moment(seg.tFDepartureData.departureDateTime).format("DD MMM YYYY")}
                                        </div>
                                        <div style={styles.airportText}>
                                          {seg.tFDepartureData.airportName}
                                          {seg.tFDepartureData.terminal && <>, T{seg.tFDepartureData.terminal}</>}
                                        </div>
                                      </div>
                                      <div style={{ textAlign: "center", flex: 1 }}>
                                        <div style={{ border: "none", borderTop: `1px dashed ${faint}`, margin: "0 12px" }}></div>
                                        <div style={{ fontSize: "12px", color: faint, fontWeight: 600, marginTop: "12px" }}>
                                          {getTimeFromMins(seg.tFAirlineData?.duration)}
                                        </div>
                                      </div>
                                      <div style={{ ...styles.timeBlock, textAlign: "right" }}>
                                        <div style={styles.timeText}>
                                          {moment(seg.tFArrivalData.arrivalDateTime).format("HH:mm")}
                                        </div>
                                        <div style={styles.dateText}>
                                          {moment(seg.tFArrivalData.arrivalDateTime).format("DD MMM YYYY")}
                                        </div>
                                        <div style={{ ...styles.airportText, textAlign: "right" }}>
                                          {seg.tFArrivalData.airportName}
                                          {seg.tFArrivalData.terminal && <>, T{seg.tFArrivalData.terminal}</>}
                                        </div>
                                      </div>
                                    </div>

                                    {seg.tFAirlineData.connectingTime !== 0 && (
                                      <div style={{ padding: "0 8px 12px" }}>
                                        <span style={styles.connectBadge}>
                                          Layover: {getTimeFromMins(seg.tFAirlineData.connectingTime)}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                ))}

                                {/* Perforated tear line */}
                                <div style={styles.perforated}></div>
                              </div>

                              {/* Passengers */}
                              <div style={styles.passengerSection}>
                                <div style={styles.sectionLabel}>Passenger Information</div>
                                <table style={styles.passTable}>
                                  <thead>
                                    <tr>
                                      <th style={styles.passTh}>#</th>
                                      <th style={styles.passTh}>Passenger Name</th>
                                      <th style={styles.passTh}>PNR</th>
                                      <th style={styles.passTh}>Baggage</th>
                                      <th style={styles.passTh}>Meals</th>
                                      <th style={styles.passTh}>Seat</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {travellers.length > 0 && travellers.map((t, idx) => (
                                      <tr key={idx}>
                                        <td style={styles.passTd}>{idx + 1}</td>
                                        <td style={{ ...styles.passTd, fontWeight: 700 }}>
                                          <div>{t.title}. {t.firstName} {t.lastName}</div>
                                          <BarcodeGenerator
                                            value={buildBoardingPassScanText(bookingDetails, t)}
                                            size={80}
                                            style={{ marginTop: 4 }}
                                          />
                                        </td>
                                        <td style={styles.passTd}>{getPnrCodes(bookingDetails.pnr) || "—"}</td>
                                        <td style={styles.passTd}>{t.dcBaggageTypes?.[0]?.description || "—"}</td>
                                        <td style={styles.passTd}>{t.dcMealTypes?.[0]?.description || "—"}</td>
                                        <td style={styles.passTd}>{t.dcSeatTypes?.[0]?.description || "—"}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>

                              {/* Footer */}
                              <div style={styles.barcodeSection}>
                                <div style={styles.bookingRef}>
                                  <div style={styles.bookingRefLabel}>Booking Reference</div>
                                  <div style={styles.bookingRefValue}>{bookingDetails.bookingNo}</div>
                                  {bookingDetails.pnr && <div style={styles.pnrText}>PNR: {bookingDetails.pnr}</div>}
                                </div>
                              </div>
                            </div>
                          ))
                        )}

                        {itinerary.length === 0 && !pageLoading && (
                          <div style={{ textAlign: "center", padding: "60px 0", color: faint }}>
                            <div>No booking details found.</div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlightVoucher;
